import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as VoiceAPI from 'api/Voice';
import { dispatch } from 'store';
import { TYPE_OF_FIELD_IN_COLUMN } from 'utils/scorecard';
import { openSnackbar } from 'store/reducers/snackbar';

const displayMessage = ({ type, msg }) => {
  dispatch(
    openSnackbar({
      open: true,
      message: msg ? msg : type === 'success' ? 'Success' : 'Something went wrong',
      variant: 'alert',
      alert: {
        color: type || 'success'
      },
      close: false
    })
  );
};

const initialState = {
  pagination: {
    pageSize: 10,
    currentPage: 1,
    totalRecords: null
  },
  column: [
    { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
    { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
    { title: 'Provider', key: 'provider', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Voice Id', key: 'voice_id', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Gender', key: 'gender', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Age Group', key: 'age_group', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Langugae', key: 'language.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Accent', key: 'accent.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Style', key: 'style.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Scope', key: 'scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
    { title: 'Subscription Plans', key: 'subscription_plan_type', field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY, width: 120 },
    { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
  ],
  data: null,
  allLanguages: [],
  allAccents: [],
  allStyles: [],
  allTTSVendors: [],
  customerVoice: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'agent_core_voice.name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Provider', key: 'agent_core_voice.provider', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Voice Id', key: 'agent_core_voice.voice_id', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      { title: 'Scope', key: 'agent_core_voice.scope', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 120 },
      {
        title: 'Subscription Plans',
        key: 'agent_core_voice.subscription_plan_type',
        field_type: TYPE_OF_FIELD_IN_COLUMN.ARRAY,
        width: 120
      },
      { title: 'Status', key: 'status', field_type: TYPE_OF_FIELD_IN_COLUMN.STATUS, width: 120 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null,
    allPublicVoices: []
  },
  language: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  accent: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  style: {
    pagination: {
      pageSize: 10,
      currentPage: 1,
      totalRecords: null
    },
    column: [
      { title: '#', key: 'id', field_type: TYPE_OF_FIELD_IN_COLUMN.NUMBER, width: 120 },
      { title: 'Name', key: 'name', field_type: TYPE_OF_FIELD_IN_COLUMN.STRING, width: 210 },
      { title: 'Action', key: 'actions', field_type: TYPE_OF_FIELD_IN_COLUMN.ACTIONS, width: 120 }
    ],
    data: null
  },
  status: '',
  ui: {
    loading: false,
    msg: ''
  }
};

//Language
const getLanguageData = createAsyncThunk('voice/getLanguageData', async ({ params, signal }) => {
  const res = await VoiceAPI.getLanguageData(params, signal);
  return res?.data?.data;
});

const createLanguage = createAsyncThunk('voice/createLanguage', async (params) => {
  try {
    const res = await VoiceAPI.createLanguage(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateLanguage = createAsyncThunk('voice/updateLanguage', async (params) => {
  try {
    const res = await VoiceAPI.updateLanguage(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteLanguage = createAsyncThunk('voice/deleteLanguage', async (id) => {
  try {
    const res = await VoiceAPI.deleteLanguage(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Accent
const getAccentData = createAsyncThunk('voice/getAccentData', async ({ params, signal }) => {
  const res = await VoiceAPI.getAccentData(params, signal);
  return res?.data?.data;
});

const createAccent = createAsyncThunk('voice/createAccent', async (params) => {
  try {
    const res = await VoiceAPI.createAccent(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateAccent = createAsyncThunk('voice/updateAccent', async (params) => {
  try {
    const res = await VoiceAPI.updateAccent(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteAccent = createAsyncThunk('voice/deleteAccent', async (id) => {
  try {
    const res = await VoiceAPI.deleteAccent(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Style
const getStyleData = createAsyncThunk('voice/getStyleData', async ({ params, signal }) => {
  const res = await VoiceAPI.getStyleData(params, signal);
  return res?.data?.data;
});

const createStyle = createAsyncThunk('voice/createStyle', async (params) => {
  try {
    const res = await VoiceAPI.createStyle(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateStyle = createAsyncThunk('voice/updateStyle', async (params) => {
  try {
    const res = await VoiceAPI.updateStyle(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteStyle = createAsyncThunk('voice/deleteStyle', async (id) => {
  try {
    const res = await VoiceAPI.deleteStyle(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

//Voice
const getVoiceData = createAsyncThunk('voice/getVoiceData', async ({ params, signal }) => {
  const res = await VoiceAPI.getVoiceData(params, signal);
  return res?.data?.data;
});

const createVoice = createAsyncThunk('voice/createVoice', async (params) => {
  try {
    const res = await VoiceAPI.createVoice(params);
    displayMessage({ type: 'success', msg: 'Created successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateVoice = createAsyncThunk('voice/updateVoice', async (params) => {
  try {
    const res = await VoiceAPI.updateVoice(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteVoice = createAsyncThunk('voice/deleteVoice', async (id) => {
  try {
    const res = await VoiceAPI.deleteVoice(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllLanguages = createAsyncThunk('voice/getAllLanguages', async () => {
  const res = await VoiceAPI.getAllLanguages();
  return res?.data?.data;
});

const getAllAccents = createAsyncThunk('voice/getAllAccents', async () => {
  const res = await VoiceAPI.getAllAccents();
  return res?.data?.data;
});

const getAllStyles = createAsyncThunk('voice/getAllStyles', async () => {
  const res = await VoiceAPI.getAllStyles();
  return res?.data?.data;
});

const getAllTTSVendors = createAsyncThunk('voice/getAllTTSVendors', async () => {
  const res = await VoiceAPI.getAllTTSVendors();
  return res?.data?.data;
});

//Customer
const getCustomerVoiceData = createAsyncThunk('voice/getCustomerVoiceData', async ({ params, signal }) => {
  const res = await VoiceAPI.getCustomerVoiceData(params, signal);
  return res?.data?.data;
});

const createCustomerVoice = createAsyncThunk('voice/createCustomerVoice', async (params) => {
  try {
    const res = await VoiceAPI.createCustomerVoice(params);
    displayMessage({ type: 'success', msg: 'Attached successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const updateCustomerVoice = createAsyncThunk('voice/updateCustomerVoice', async (params) => {
  try {
    const res = await VoiceAPI.updateCustomerVoice(params);
    displayMessage({ type: 'success', msg: 'Updated successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const deleteCustomerVoice = createAsyncThunk('voice/deleteCustomerVoice', async (id) => {
  try {
    const res = await VoiceAPI.deleteCustomerVoice(id);
    displayMessage({ type: 'success', msg: 'Deleted successfully' });
    return res.data?.data;
  } catch (error) {
    displayMessage({ type: 'error', msg: error?.message });
  }
});

const getAllPublicVoices = createAsyncThunk('voice/getAllPublicVoices', async () => {
  const res = await VoiceAPI.getAllPublicVoices();
  return res?.data?.data;
});

const VoiceSlice = createSlice({
  name: 'voice',
  initialState: initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    setVoiceCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setVoicePageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    },

    //Customer
    setCustomerVoiceCurrentPage: (state, action) => {
      state.customerVoice.pagination.currentPage = action.payload;
    },
    setCustomerVoicePageSize: (state, action) => {
      state.customerVoice.pagination.pageSize = action.payload;
    },

    //Language
    setLanguageCurrentPage: (state, action) => {
      state.language.pagination.currentPage = action.payload;
    },
    setLanguagePageSize: (state, action) => {
      state.language.pagination.pageSize = action.payload;
    },
    //Accent
    setAccentCurrentPage: (state, action) => {
      state.accent.pagination.currentPage = action.payload;
    },
    setAccentPageSize: (state, action) => {
      state.accent.pagination.pageSize = action.payload;
    },
    //Style
    setStyleCurrentPage: (state, action) => {
      state.style.pagination.currentPage = action.payload;
    },
    setStylePageSize: (state, action) => {
      state.style.pagination.pageSize = action.payload;
    }
  },
  extraReducers: {
    [getAllPublicVoices.fulfilled]: (state, action) => {
      state.customerVoice.allPublicVoices = action.payload;
    },
    [getAllLanguages.fulfilled]: (state, action) => {
      state.allLanguages = action.payload;
    },
    [getAllAccents.fulfilled]: (state, action) => {
      state.allAccents = action.payload;
    },
    [getAllStyles.fulfilled]: (state, action) => {
      state.allStyles = action.payload;
    },
    [getAllTTSVendors.fulfilled]: (state, action) => {
      state.allTTSVendors = action.payload;
    },
    [getVoiceData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getVoiceData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.pagination.totalRecords = action.payload?.count;
      state.data = action.payload?.results || [];
    },
    [getVoiceData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createVoice.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateVoice.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      const updatedData = state.data.map((item) => {
        if (item.id === action.payload.id) {
          return action.payload;
        }
        return item;
      });
      state.data = updatedData;
    },

    [updateVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteVoice.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getCustomerVoiceData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getCustomerVoiceData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.customerVoice.pagination.totalRecords = action.payload?.count;
      state.customerVoice.data = action.payload?.results || [];
    },
    [getCustomerVoiceData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createCustomerVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [createCustomerVoice.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [createCustomerVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [updateCustomerVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [updateCustomerVoice.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [updateCustomerVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [deleteCustomerVoice.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },

    [deleteCustomerVoice.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },

    [deleteCustomerVoice.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getLanguageData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getLanguageData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.language.pagination.totalRecords = action.payload?.count;
      state.language.data = action.payload?.results || [];
    },
    [getLanguageData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [createLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [updateLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [updateLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [deleteLanguage.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteLanguage.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteLanguage.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getAccentData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getAccentData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.accent.pagination.totalRecords = action.payload?.count;
      state.accent.data = action.payload?.results || [];
    },
    [getAccentData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createAccent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createAccent.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [createAccent.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [updateAccent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateAccent.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [updateAccent.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [deleteAccent.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteAccent.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteAccent.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },

    [getStyleData.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [getStyleData.fulfilled]: (state, action) => {
      state.status = 'idle';
      state.ui.loading = false;
      state.style.pagination.totalRecords = action.payload?.count;
      state.style.data = action.payload?.results || [];
    },
    [getStyleData.rejected]: (state, action) => {
      state.status = 'error';
      state.ui.loading = false;
      state.error = action.error.message;
    },
    [createStyle.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [createStyle.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [createStyle.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [updateStyle.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [updateStyle.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [updateStyle.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    },
    [deleteStyle.pending]: (state) => {
      state.status = 'init';
      state.ui.loading = true;
    },
    [deleteStyle.fulfilled]: (state) => {
      state.status = 'idle';
      state.ui.loading = false;
    },
    [deleteStyle.rejected]: (state, action) => {
      state.status = 'error';
      state.error = action.error.message;
      state.ui.loading = false;
    }
  }
});

const {
  resetState,
  setVoiceCurrentPage,
  setVoicePageSize,
  setCustomerVoiceCurrentPage,
  setCustomerVoicePageSize,
  setLanguageCurrentPage,
  setLanguagePageSize,
  setAccentCurrentPage,
  setAccentPageSize,
  setStyleCurrentPage,
  setStylePageSize
} = VoiceSlice.actions;

export default VoiceSlice.reducer;

export {
  resetState,
  setVoiceCurrentPage,
  setVoicePageSize,
  getVoiceData,
  createVoice,
  updateVoice,
  deleteVoice,
  getAllLanguages,
  getAllAccents,
  getAllStyles,
  getAllTTSVendors,
  //Customer
  setCustomerVoiceCurrentPage,
  setCustomerVoicePageSize,
  getAllPublicVoices,
  getCustomerVoiceData,
  createCustomerVoice,
  updateCustomerVoice,
  deleteCustomerVoice,
  //Language
  setLanguageCurrentPage,
  setLanguagePageSize,
  getLanguageData,
  createLanguage,
  updateLanguage,
  deleteLanguage,
  //Accent
  setAccentCurrentPage,
  setAccentPageSize,
  getAccentData,
  createAccent,
  updateAccent,
  deleteAccent,
  //Style
  setStyleCurrentPage,
  setStylePageSize,
  getStyleData,
  createStyle,
  updateStyle,
  deleteStyle
};
